import React from 'react';
import '../BookingOverlay.css'
import {ChooseTime} from "./ChooseTime";
import {IApiBusinessProfileProto} from "../../../compiled";
import {ChooseService} from "./ChooseService";
import {ChooseLocation} from "./ChooseLocation";
import {ChoosePractitioner} from "./ChoosePractitioner";
import {useSearchParams} from "react-router-dom";
import {BookingFlowPage} from "../BookingFlowPage";
import {findLocation, findService} from "../BusinessUtil";
import {findPractitioner} from "../../../util/ProviderUtil";
import {priceToString} from "../../../util/PriceUtil";
import {SelectedBooking} from "../BookingFlow";

type Props = {
  business: IApiBusinessProfileProto
  onChosen: (booking: SelectedBooking) => void;
}

/**
 * Booking flow for appointments. This uses URL params for the various fields to
 * make the navigation browser-friendly and intuitive. It also allows linking to a
 * provider or location from a provider website.
 */
export const AppointmentBookingFlow = (props: Props) => {

  const [searchParams, setSearchParams] = useSearchParams();

  // All information is selected as a search param. This makes browser navigation work
  // and it makes all these pages directly linkable.
  const serviceSku = searchParams.get('s') ?? undefined;
  const providerId = searchParams.get('p') ?? undefined;
  const locationId = searchParams.get('l') ?? undefined;
  const timestampParam = searchParams.get('t') ?? undefined;

  const business = props.business;
  const location = findLocation(business, locationId);
  const service = findService(business, serviceSku);
  const virtualAppt = service && service.isVirtual;
  const practitioner = business && findPractitioner(business.providers, providerId)
  const timestamp = timestampParam && parseInt(timestampParam);

  const setParam = (k: string, v: string | null | undefined) => {
    setSearchParams(existing => {
      const newSearchParams = new URLSearchParams(existing);
      if (v) {
        newSearchParams.set(k, v);
      } else {
        newSearchParams.delete(k);
      }
      return newSearchParams;
    });
  }

  const clearParams = (k: string[]) => {
    setSearchParams(existing => {
      const newSearchParams = new URLSearchParams(existing);
      k.forEach(key => newSearchParams.delete(key));
      return newSearchParams;
    });
  }

  const getLeftNav = () => {

    if (!practitioner && !service && !location && !timestamp) {

      // Nothing has been selected! In this case, don't show the left nav at all.
      // Once the service is selected, then show the left nav.
      return <></>
    }

    return <div className={'BookingOverlayBottomLeft BookingOverlaySectionMargin'}>
      {service && <div>
        <div><b>{service.name} ({service.lengthMinutes} min)</b>
        </div>
        <button className={'BookingChangeLink'} onClick={() => {
          clearParams(['s', 't', 'l', 'p'])
        }}>
          Change service
        </button>
      </div>}

      {location && <div>
        <div><b>{location.locationName}</b></div>
        <div>{location.physicalLocation?.address}</div>
        <button className={'BookingChangeLink'} onClick={() => {
          clearParams(['l', 't', 'p'])
        }}>Change location
        </button>
      </div>}

      {virtualAppt && <div>
        <div><b>Virtual appointment</b></div>
        <div>Meet with your practitioner via Zoom</div>
      </div>}

      {practitioner && <div>
        <div><b>{practitioner.firstName} {practitioner.lastName}</b></div>
        <div>{practitioner.jobTitle}</div>
        <button className={'BookingChangeLink'} onClick={() => {
          clearParams(['p', 't'])
        }}>Change practitioner
        </button>
      </div>}

      {timestamp && <div>
        <div><b>{new Date(timestamp).toLocaleString(undefined, {
          dateStyle: 'long', timeStyle: 'short'
        })}</b></div>
        <button className={'BookingChangeLink'} onClick={() => {
          clearParams(['t'])
        }}>Change time
        </button>
      </div>}
    </div>
  }

  if (!service) {
    return <BookingFlowPage
        title={`Book with ${business?.proto?.businessName}`}
        leftNav={getLeftNav()}
        content={<ChooseService business={business}
                                practitionerFilter={practitioner?.practitionerId}
                                onSelect={s => setParam('s', s.sku)}/>}
    />
  }

  // Skip this step for virtual appointments
  if (!service.isVirtual && !location && !virtualAppt) {
    return <BookingFlowPage
        title={`Book with ${business?.proto?.businessName}`}
        leftNav={getLeftNav()}
        content={<ChooseLocation service={service}
                                 businessProfile={business}
                                 onSelect={l => setParam('l', l.locationId)}/>}
    />
  }

  if (!practitioner) {
    return <BookingFlowPage
        title={`Book with ${business?.proto?.businessName}`}
        leftNav={getLeftNav()}
        content={<ChoosePractitioner locationFilter={location}
                                     serviceFilter={service}
                                     business={business}
                                     onSelect={p => setParam('p', p.practitionerId)}/>}
    />
  }


  if (!timestamp) {
    return <BookingFlowPage
        title={`Book with ${business?.proto?.businessName}`}
        leftNav={getLeftNav()}
        content={<ChooseTime businessId={business.businessId as string}
                             serviceSku={service.sku || ''}
                             practitionerId={practitioner.practitionerId as string}
                             onSelected={t => {
                               // NOTE: intentionally do NOT update the parameter here,
                               // because the callback also updates url query parameters
                               // and doing this twice in one frame is bad.
                               props.onChosen({
                                 appointment: {
                                   timestamp: t,
                                   location: location,
                                   virtualAppt: virtualAppt,
                                   practitioner: practitioner,
                                   service: service
                                 }
                               })
                             }}/>}
    />
  }

  return <></>
}